// AssetIdentity — enforces the app-wide Asset ID / Cat No display rule (handoff §6).
//   roomy  -> "AssetId (CatNo)"   (cards, headers, detail rows, option rows)
//   tight  -> "AssetId" only       (chips, dense single-line cells)
// Never renders blank: falls back to whichever of AssetId / CatNo exists.
// AssetId renders in mono (the load-bearing "scannable identifier" convention).
// Accepts an `asset` object (camelCase or snake_case) or explicit assetId/catNo.
//
//   <AssetIdentity asset={row} roomy />            // AST-04821 (CAT-7750-LH)
//   <AssetIdentity assetId="AST-04821" />          // tight -> AST-04821
//   <AssetIdentity asset={a} size="block" roomy /> // big id + faint cat no under

import React from 'react';
import { assetPrimaryLabel, assetFullLabel } from '../../shared';

export default function AssetIdentity({
  asset,
  assetId,
  catNo,
  roomy = false,
  size = 'inline', // 'inline' | 'block'
  idStyle,
  catStyle,
  style,
  ...rest
}) {
  const a = asset || { assetId, catNo };
  const id = assetPrimaryLabel(a); // AssetId (fallback CatNo)
  const full = assetFullLabel(a); // "AssetId (CatNo)"
  const hasBoth = full.includes('(');

  // Block layout: prominent mono id, faint mono cat no beneath (roomy only).
  if (size === 'block') {
    const catOnly = hasBoth ? full.slice(full.indexOf('(')) : null;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', ...style }} {...rest}>
        <span
          className="aims-mono"
          style={{ fontWeight: 600, color: 'var(--text)', lineHeight: 1.15, ...idStyle }}
        >
          {id}
        </span>
        {roomy && catOnly && (
          <span
            className="aims-mono"
            style={{ fontSize: '0.62em', color: 'var(--faint)', lineHeight: 1.2, ...catStyle }}
          >
            {catOnly}
          </span>
        )}
      </span>
    );
  }

  // Inline layout.
  if (roomy && hasBoth) {
    const idPart = full.slice(0, full.indexOf('(')).trim();
    const catPart = full.slice(full.indexOf('('));
    return (
      <span
        className="aims-mono"
        style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, ...style }}
        {...rest}
      >
        <span style={{ fontWeight: 600, color: 'var(--text)', ...idStyle }}>{idPart}</span>
        <span style={{ color: 'var(--faint)', fontSize: '0.82em', ...catStyle }}>{catPart}</span>
      </span>
    );
  }

  return (
    <span
      className="aims-mono"
      style={{ fontWeight: 600, color: 'var(--text)', ...style, ...idStyle }}
      {...rest}
    >
      {id}
    </span>
  );
}
